import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  Grid,
  InputAdornment,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CalculateIcon from "@mui/icons-material/Calculate";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { useTranslation } from "react-i18next";
import axios from "axios";
import BentoCard from "components/bento/BentoCard";
import { fieldSx, sectionLabelSx } from "components/bento/BentoFormControls";
import {
  BG_TINT, BORDER, BORDER_PURPLE, DIVIDER, PURPLE_DARKER, PURPLE_LIGHT,
  RADIUS_TILE, TEXT_BODY, TEXT_SECONDARY, TEXT_SUBTLE, tableHeaderSx,
} from "components/bento/bentoTokens";
// Same calculation the public/customer calculator uses (shared/shippingCalc.js).
import { calculateShipping } from "../generated/shippingCalc";

const RULE_META = {
  overweight: { key: "shippingCalculator.rule.overweight", color: "#f59e0b", icon: <TrendingUpIcon sx={{ fontSize: 15 }} /> },
  normal: { key: "shippingCalculator.rule.normal", color: "#10b981", icon: <LocalShippingIcon sx={{ fontSize: 15 }} /> },
  discount1: { key: "shippingCalculator.rule.discount1", color: "#3b82f6", icon: <TrendingDownIcon sx={{ fontSize: 15 }} /> },
  discount2: { key: "shippingCalculator.rule.discount2", color: "#8b5cf6", icon: <TrendingDownIcon sx={{ fontSize: 15 }} /> },
};

const Stat = ({ label, children }) => (
  <Grid item xs={6} sm={3}>
    <Typography sx={{ fontSize: 11, opacity: 0.75, letterSpacing: ".04em" }}>{label}</Typography>
    <Typography sx={{ fontSize: 14, fontWeight: 700, mt: "2px" }}>{children}</Typography>
  </Grid>
);

/**
 * Staff-facing shipping cost calculator (bento styling).
 *
 * Self-contained: fetches the live rate table from /api/public/shipping-rates and
 * prices through the shared calculation, so it can be dropped into any staff page
 * (currently scenes/calculator; reusable inside dialogs/forms later).
 */
const ShippingCalculatorPanel = ({ showRateTable = true }) => {
  const { t } = useTranslation();
  const [rates, setRates] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dims, setDims] = useState({ l: "", w: "", h: "" });
  const [weight, setWeight] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get("/api/public/shipping-rates");
        if (cancelled) return;
        if (res.data?.success) setRates(res.data);
        else setError(res.data?.message || "Failed to load rates");
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const result = useMemo(() => calculateShipping(rates, dims, weight), [rates, dims, weight]);

  const currency = rates?.currency || "THB";
  const sizeUnit = rates?.sizeUnit || "cm";
  const weightUnit = rates?.weightUnit || "kg";
  const rule = result && !result.oversize ? RULE_META[result.rule] : null;

  return (
    <Grid container spacing={2.5}>
      {/* Input + result */}
      <Grid item xs={12} md={showRateTable ? 7 : 12}>
        <BentoCard title={t("shippingCalculator.parcelDetails")} bodySx={{ p: "22px 24px 24px" }}>
          <Box sx={{ ...sectionLabelSx, mb: "14px" }}>
            {t("shippingCalculator.dimensions")} ({sizeUnit})
          </Box>
          <Grid container spacing={2}>
            {[
              { key: "l", label: t("shippingCalculator.length") },
              { key: "w", label: t("shippingCalculator.width") },
              { key: "h", label: t("shippingCalculator.height") },
            ].map((f) => (
              <Grid item xs={4} key={f.key}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label={f.label}
                  value={dims[f.key]}
                  onChange={(e) => setDims({ ...dims, [f.key]: e.target.value })}
                  inputProps={{ min: 0, step: "any" }}
                  InputProps={{ endAdornment: <InputAdornment position="end">{sizeUnit}</InputAdornment> }}
                  sx={fieldSx}
                />
              </Grid>
            ))}
          </Grid>

          <Box sx={{ ...sectionLabelSx, mt: "22px", mb: "14px" }}>{t("shippingCalculator.weight")}</Box>
          <TextField
            fullWidth
            size="small"
            type="number"
            label={t("shippingCalculator.weight")}
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            inputProps={{ min: 0, step: "any" }}
            InputProps={{ endAdornment: <InputAdornment position="end">{weightUnit}</InputAdornment> }}
            sx={fieldSx}
          />

          <Box sx={{ mt: "22px" }}>
            {loading && <LinearProgress color="secondary" sx={{ borderRadius: 999 }} />}
            {error && <Alert severity="error" sx={{ borderRadius: "12px" }}>{error}</Alert>}

            {!loading && !error && !result && (
              <Box sx={{
                textAlign: "center", py: "30px", borderRadius: RADIUS_TILE,
                border: `1.5px dashed ${BORDER_PURPLE}`, background: BG_TINT,
              }}>
                <CalculateIcon sx={{ fontSize: 34, color: BORDER_PURPLE, mb: "6px" }} />
                <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY }}>
                  {t("shippingCalculator.enterDetails")}
                </Typography>
              </Box>
            )}

            {result?.oversize && (
              <Alert severity="warning" sx={{ borderRadius: "12px" }}>
                {t("shippingCalculator.oversizeStaff", {
                  total: result.totalSize,
                  max: result.maxSize,
                  unit: sizeUnit,
                })}
              </Alert>
            )}

            {result && !result.oversize && (
              <Box sx={{
                borderRadius: RADIUS_TILE, p: "20px 22px", color: "#fff",
                background: `linear-gradient(135deg,${PURPLE_LIGHT} 0%,${PURPLE_DARKER} 100%)`,
                boxShadow: "0 14px 30px -18px rgba(86,40,160,.9)",
              }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} mb="10px">
                  <Typography sx={{ fontSize: 12.5, fontWeight: 600, opacity: 0.85, letterSpacing: ".04em" }}>
                    {t("shippingCalculator.estimatedCost")}
                  </Typography>
                  {rule && (
                    <Chip
                      size="small"
                      icon={rule.icon}
                      label={t(rule.key)}
                      sx={{
                        height: 24, fontSize: 11.5, fontWeight: 700, color: "#fff",
                        bgcolor: "rgba(255,255,255,.22)",
                        "& .MuiChip-icon": { color: "#fff" },
                      }}
                    />
                  )}
                </Stack>

                <Stack direction="row" alignItems="baseline" spacing={1}>
                  <Typography sx={{ fontSize: 42, fontWeight: 800, lineHeight: 1 }}>
                    {result.total.toLocaleString()}
                  </Typography>
                  <Typography sx={{ fontSize: 18, fontWeight: 600, opacity: 0.85 }}>{currency}</Typography>
                </Stack>

                <Box sx={{ height: "1px", background: "rgba(255,255,255,.22)", my: "16px" }} />

                <Grid container spacing={2}>
                  <Stat label={t("shippingCalculator.totalSize")}>
                    {result.totalSize} {sizeUnit}
                  </Stat>
                  <Stat label={t("shippingCalculator.matchedTier")}>
                    #{result.tier.tier} · ≤{result.tier.size}{sizeUnit}
                  </Stat>
                  <Stat label={t("shippingCalculator.basePrice")}>
                    {result.basePrice.toLocaleString()} {currency}
                    {result.appliedTier.tier !== result.tier.tier && (
                      <Typography component="span" sx={{ fontSize: 11, opacity: 0.8 }}>
                        {" "}(#{result.appliedTier.tier})
                      </Typography>
                    )}
                  </Stat>
                  {result.rule === "overweight" && (
                    <Stat label={t("shippingCalculator.overweightCharge")}>
                      +{result.extra.toLocaleString()} {currency}
                      <Typography component="span" sx={{ fontSize: 11, opacity: 0.8 }}>
                        {" "}({result.extraKg.toFixed(2)}{weightUnit} × {rates.overweightPerKg})
                      </Typography>
                    </Stat>
                  )}
                </Grid>
              </Box>
            )}
          </Box>
        </BentoCard>
      </Grid>

      {/* How it works */}
      <Grid item xs={12} md={showRateTable ? 5 : 12}>
        <BentoCard title={t("shippingCalculator.howItWorks")} bodySx={{ p: "20px 24px 24px" }} sx={{ height: "100%" }}>
          <Stack spacing={1.8}>
            {[
              { color: "#667eea", text: t("shippingCalculator.help.size") },
              { color: "#f59e0b", text: t("shippingCalculator.help.overweight", { perKg: rates?.overweightPerKg ?? 10, currency }) },
              { color: "#10b981", text: t("shippingCalculator.help.normal") },
              { color: "#3b82f6", text: t("shippingCalculator.help.discount1") },
              { color: "#8b5cf6", text: t("shippingCalculator.help.discount2") },
            ].map((item, i) => (
              <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
                <Box sx={{
                  minWidth: 24, height: 24, borderRadius: "50%", flexShrink: 0, mt: "1px",
                  bgcolor: `${item.color}1f`, color: item.color,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontWeight: 700, fontSize: 12,
                }}>
                  {i + 1}
                </Box>
                <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY, lineHeight: 1.65 }}>{item.text}</Typography>
              </Stack>
            ))}
          </Stack>
        </BentoCard>
      </Grid>

      {/* Rate table */}
      {showRateTable && (
        <Grid item xs={12}>
          <BentoCard
            title={t("shippingCalculator.rateTable")}
            headerRight={
              <Typography sx={{ fontSize: 11.5, color: TEXT_SUBTLE }}>
                {t("shippingCalculator.rateTableHint")}
              </Typography>
            }
          >
            <TableContainer sx={{ maxHeight: 440 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...tableHeaderSx, bgcolor: "#fff", borderBottom: `1px solid ${DIVIDER}` }}>#</TableCell>
                    <TableCell align="right" sx={{ ...tableHeaderSx, bgcolor: "#fff", borderBottom: `1px solid ${DIVIDER}` }}>
                      {t("shippingCalculator.price")} ({currency})
                    </TableCell>
                    <TableCell align="right" sx={{ ...tableHeaderSx, bgcolor: "#fff", borderBottom: `1px solid ${DIVIDER}` }}>
                      {t("shippingCalculator.weight")} ({weightUnit})
                    </TableCell>
                    <TableCell align="right" sx={{ ...tableHeaderSx, bgcolor: "#fff", borderBottom: `1px solid ${DIVIDER}` }}>
                      {t("shippingCalculator.size")} ({sizeUnit})
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(rates?.tiers || []).map((row) => {
                    const isMatch = result && !result.oversize && result.tier.tier === row.tier;
                    const isApplied = result && !result.oversize && result.appliedTier.tier === row.tier;
                    return (
                      <TableRow
                        key={row.tier}
                        sx={{
                          bgcolor: isApplied ? "rgba(124,58,237,.10)" : isMatch ? "rgba(124,58,237,.04)" : "transparent",
                          transition: "background .2s ease",
                          "& td": { fontSize: 13, color: TEXT_BODY, borderBottom: `1px solid ${BORDER}` },
                        }}
                      >
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Box component="span" sx={{ fontWeight: isApplied ? 700 : 500 }}>{row.tier}</Box>
                            {isApplied && (
                              <Chip
                                label={t("shippingCalculator.applied")}
                                size="small"
                                color="secondary"
                                sx={{ height: 18, fontSize: "0.65rem", fontWeight: 700 }}
                              />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: isApplied ? 700 : 400 }}>
                          {row.price.toLocaleString()}
                        </TableCell>
                        <TableCell align="right">{row.weight}</TableCell>
                        <TableCell align="right">{row.size}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </BentoCard>
        </Grid>
      )}
    </Grid>
  );
};

export default ShippingCalculatorPanel;
